/**
 * Document macros for equations: parse definitions, validate a macro table, expand macros in TeX
 * and write a LaTeX preamble.
 *
 * This file is self-contained on purpose (no imports), so the same file can be shared byte for byte
 * between packages. It works on plain strings and plain objects only.
 *
 * A macro table maps a name without the backslash to `{ args, body }`. It is untrusted input
 * (it comes from template configuration and document metadata), so every reader here checks keys
 * with own-property tests and never assigns through a key it has not validated.
 */

/** A document macro: how many `#1`..`#9` arguments it takes and its TeX body. */
export type MathMacro = { args: number; body: string };

/** A table of macros keyed by name without the backslash. */
export type MathMacroTable = Record<string, MathMacro>;

export type MathMacroProblemCode =
  | 'name-invalid'
  | 'name-reserved-object'
  | 'name-builtin'
  | 'args-invalid'
  | 'body-invalid'
  | 'body-too-large'
  | 'body-control-chars'
  | 'body-label'
  | 'recursive'
  | 'table-too-large'
  | 'entry-invalid'
  | 'not-an-object'
  | 'unbalanced'
  | 'optional-argument'
  | 'unsupported-parameters'
  | 'syntax';

/** One problem with a macro or a definition. `name` is null for problems of the whole table. */
export interface MathMacroProblem {
  name: string | null;
  code: MathMacroProblemCode;
  /** Extra text for the message (for example the offending definition), when there is some. */
  detail?: string;
}

/** Largest body, in characters. */
export const MAX_MACRO_BODY_LENGTH = 4096;
/** Largest table, in characters of names and bodies together (about 64 KB). */
export const MAX_MACRO_TABLE_SIZE = 65536;
/** Longest macro name. */
export const MAX_MACRO_NAME_LENGTH = 64;
/** Deepest nesting of macros inside macros that `expandMacros` follows. */
export const MAX_MACRO_DEPTH = 24;
/** Longest result `expandMacros` produces; a longer one leaves the TeX unexpanded. */
export const MAX_EXPANDED_LENGTH = 262144;
/** Most macro calls `expandMacros` expands in one equation; more leaves the TeX unexpanded. Counts calls that expand to nothing too. */
export const MAX_EXPANSION_STEPS = 20000;

/**
 * Control sequence names that TeX, LaTeX and the MathJax packages used for export already define,
 * so a document macro must not reuse them. Generated once from the macro, character and
 * delimiter maps of the MathJax 4.1 input packages base, ams, action, bbm, begingroup, braket,
 * bussproofs, cancel, centernot, color, colortbl, dsfont, empheq, extpfeil, gensymb, html,
 * mathtools, mhchem, newcommand, physics, unicode, units and upgreek (letters-only names), plus the
 * TeX and LaTeX primitives for definitions, labels and conditionals. The text-mode symbol sets
 * (textcomp, textmacros) and the blackboard letter set (bboldx) are left out to keep the list
 * small. A name missing here is not a promise that MathJax lacks it; it only means the macro is
 * allowed, and the expansion happens before MathJax sees the TeX.
 */
const BUILTIN_NAME_LIST = `
AA AXC Aboxed And Approxcolon ArrowBetweenLines Axiom AxiomC BIC Bbb Bbbk Big Bigg Biggl Biggm
Biggr Bigl Bigm Bigr BinaryInf BinaryInfC Box Bqty Bra Braket Bumpeq Cap Colonapprox Colondash
Coloneq Coloneqq Colonsim Cup Dashcolon DeclareMathOperator DeclarePairedDelimiter
DeclarePairedDelimiterX DeclarePairedDelimiterXPP DeclarePairedDelimiters
DeclarePairedDelimitersX DeclarePairedDelimitersXPP DeclareRobustCommand Delta Diamond Doteq
Eqcolon Eqqcolon Finv Game Gamma HUGE Huge Im Join Ket Ketbra LARGE LL LaTeX Lambda Large
LeftLabel Leftarrow Leftrightarrow Lleftarrow Longleftarrow Longleftrightarrow Longrightarrow
Lsh MTFlushSpaceAbove MTFlushSpaceBelow MTThinColon MakeAboxedCommand MoveEqLeft Newextarrow
Omega PV Phi Pi Pmqty Pr Probability Psi QuaternaryInf QuaternaryInfC QuinaryInf QuinaryInfC RL
Re Res Residue RightLabel Rightarrow Rrightarrow Rsh Rule S SMALL Set Sigma Simcolon Small Space
Subset Supset TIC TeX Theta Tiny Tr Trace TrinaryInf TrinaryInfC U UIC UnaryInf UnaryInfC
Updelta Upgamma Uplambda Upomega Upphi Uppi Uppsi Upsigma Upsilon Uptheta Upupsilon Upxi Vdash
Vvdash Xi above abovewithdelims abs absolutevalue acomm acos acosecant acosine acot acotangent
acsc acute adjustlimits admat aleph allowbreak alpha alwaysDashedLine alwaysNoLine
alwaysRootAtBottom alwaysRootAtTop alwaysSingleLine alwaysSolidLine amalg angle anticommutator
antidiagonalmatrix approx approxcolon approxeq arccos arccosecant arccosine arccot arccotangent
arccsc arcsec arcsecant arcsin arcsine arctan arctangent arg array asec asecant asin asine ast
asymp atan atangent atop atopwithdelims backepsilon backprime backsim backsimeq backslash
badbreak bar barwedge bcancel because begin begingroup begingroupReset begingroupSandbox beta
beth between bf big bigcap bigcirc bigcup bigg biggl biggm biggr bigl bigm bigodot bigoplus
bigotimes bigr bigsqcup bigstar bigtimes bigtriangledown bigtriangleup biguplus bigvee bigwedge
binom blacklozenge blacksquare blacktriangle blacktriangledown blacktriangleleft
blacktriangleright bmod bmqty bot bowtie boxdot boxed boxminus boxplus boxtimes bqty bra brace
brack braket break breakAlign breve buildrel bullet bumpeq cal cancel cancelto cap cases cdot
cdotp cdots ce cellcolor celsius centerOver centerdot centernot cfrac char check checkmark chi
choose circ circeq circlearrowleft circlearrowright circledR circledS circledast circledcirc
circleddash clap class clubsuit colon colonapprox colondash coloneq coloneqq colonsim color
colorbox columncolor comm commutator complement cong coprod cos cosecant cosh cosine cot
cotangent coth cp cr cramped crampedclap crampedllap crampedrlap crampedsubstack cross
crossproduct csc csch csname cssId cup curl curlyeqprec curlyeqsucc curlyvee curlywedge
curvearrowleft curvearrowright dagger daleth dashcolon dashedLine dashleftarrow dashrightarrow
dashv data dbinom dblcolon dd ddagger ddddot dddot ddot ddots def definecolor deg degree delta
derivative det determinant dfrac diagdown diagonalmatrix diagup diamond diamondsuit diffd
differential digamma dim displaylines displaystyle div divergence divideontimes divisionsymbol
divsymbol dmat document documentclass dot doteq doteqdot dotplus dotproduct dots dotsb dotsc
dotsi dotsm dotso doublebarwedge doublecap doublecup downdownarrows downharpoonleft
downharpoonright dv dyad edef ell else empheqbigl empheqbiglVert empheqbiglangle empheqbiglbrace
empheqbiglbrack empheqbiglceil empheqbiglfloor empheqbiglparen empheqbiglvert empheqbigr
empheqbigrVert empheqbigrangle empheqbigrbrace empheqbigrbrack empheqbigrceil empheqbigrfloor
empheqbigrparen empheqbigrvert empheql empheqlVert empheqlangle empheqlbrace empheqlbrack
empheqlceil empheqlfloor empheqlparen empheqlvert empheqr empheqrVert empheqrangle empheqrbrace
empheqrbrack empheqrceil empheqrfloor empheqrparen empheqrvert emptyset end endcsname endgroup
enspace ensuremath epsilon eqalign eqalignno eqcirc eqcolon eqqcolon eqref eqsim eqslantgtr
eqslantless equiv erf eta eth ev eval evaluated exists exp expandafter expectationvalue
exponential expval fCenter fallingdotseq fbox fcolorbox fderivative fdv fi flat flatfrac
footnotesize forall frac frak framebox frown functionalderivative futurelet gamma gcd gdef ge
genfrac geq geqq geqslant gets gg ggg gggtr gimel global gnapprox gneq gneqq gnsim goodbreak
grad gradient gradientnabla grave gt gtrapprox gtrdot gtreqless gtreqqless gtrless gtrsim
gvertneqq hat hbar hbox hdashline heartsuit hfil hfill hfilll hline hom hookleftarrow
hookrightarrow hphantom href hsize hskip hslash hspace huge hypcosecant hypcosine hypcotangent
hypsecant hypsine hyptangent iddots identitymatrix idotsint if iff iffalse iftrue ifx iiiint
iiint iint imaginary imat imath impliedby implies in include inf infty injlim innerproduct input
int intercal intop iota ip it item jmath jobname kappa ker kern ket ketbra label lambda land
laplacian large ldotp ldots le leadsto left leftarrow leftarrowtail leftharpoondown
leftharpoonup leftleftarrows leftrightarrow leftrightarrows leftrightharpoons
leftrightsquigarrow leftroot leftthreetimes leq leqalignno leqq leqslant lessapprox lessdot
lesseqgtr lesseqqgtr lessgtr lesssim let lg lhd lim liminf limits limsup linebreak ll llap lll
llless ln lnapprox lneq lneqq lnot lnsim log logarithm longleftarrow longleftrightarrow
longmapsto longrightarrow looparrowleft looparrowright lor lower lozenge lt ltimes lvertneqq
makebox maltese mapsto mathbb mathbbm mathbbmss mathbbmtt mathbf mathbfcal mathbffrak mathbfit
mathbfscr mathbfsf mathbfsfit mathbfsfup mathbfup mathbin mathcal mathchoice mathclap mathclose
mathds mathfrak mathinner mathit mathllap mathmakebox mathmbox mathnormal mathop mathopen
mathord mathpunct mathrel mathring mathrlap mathrm mathscr mathsf mathsfit mathsfup mathstrut
mathtip mathtoolsset mathtt mathup mathversion matrix matrixdeterminant matrixel matrixelement
matrixquantity max mbox mdet meaning measuredangle mel mhchemBondDTD mhchemBondTD mhchemBondTDD
mhchemleftarrow mhchemleftrightarrow mhchemlongLeftrightharpoons mhchemlongRightleftharpoons
mhchemlongleftarrow mhchemlongleftrightarrow mhchemlongleftrightarrows mhchemlongrightarrow
mhchemlongrightleftharpoons mhchemrightarrow mhchemxLeftrightharpoons mhchemxRightleftharpoons
mhchemxleftarrow mhchemxleftrightarrow mhchemxleftrightarrows mhchemxrightarrow
mhchemxrightleftharpoons mho micro mid middle min mit mkern mmlToken mod models moveleft
moveright mp mqty mskip mspace mu multimap nLeftarrow nLeftrightarrow nRightarrow nVDash nVdash
nabla natural naturallogarithm ncong ndownarrow ne nearrow neg negmedspace negthickspace
negthinspace neq newcolumntype newcommand newenvironment newline newtagform nexists ngeq ngeqq
ngeqslant ngtr ni nicefrac nleftarrow nleftrightarrow nleq nleqq nleqslant nless nmid noLine
nobreak nobreakspace noexpand nolimits nonscript nonumber norm normalsize not notChar notag
notin nparallel nprec npreceq nrightarrow nshortmid nshortparallel nsim nsubseteq nsubseteqq
nsucc nsucceq nsupseteq nsupseteqq ntriangleleft ntrianglelefteq ntriangleright ntrianglerighteq
nu number nuparrow nvDash nvdash nwarrow odot ohm oiiint oiint oint ointop oldstyle omega
omicron ominus op operatorname oplus order ordinarycolon oslash otimes outerproduct over
overbrace overbracket overleftarrow overleftrightarrow overline overparen overrightarrow overset
overunderset overwithdelims owns pagebreak par parallel parbox partial partialderivative
paulimatrix pb pderivative pdv perp perthousand phantom phi pi pitchfork pm pmat pmatrix pmb
pmod pmqty pod poissonbracket pqty prec precapprox preccurlyeq preceq precnapprox precneqq
precnsim precsim prescript prime principalvalue prod projlim propto providecommand psi pu pv
qall qand qas qassume qc qcc qcomma qelse qeven qfor qgiven qif qin qinteger qlet qodd qor
qotherwise qq qqtext qquad qsince qthen qty quad quantity qunless qusing raise rank real ref
refeq relax renewcommand renewenvironment renewtagform restriction rhd rho right rightarrow
rightarrowtail rightharpoondown rightharpoonup rightleftarrows rightleftharpoons
rightrightarrows rightsquigarrow rightthreetimes risingdotseq rlap rm root rootAtBottom
rootAtTop rowcolor rtimes rule sPmqty sbmqty scriptscriptstyle scriptsize scriptstyle searrow
sec secant sech set setminus sf sharp shortmid shortparallel shortvdotswithin shoveleft
shoveright sideset sigma sim simcolon simeq sin sine singleLine sinh skew small smallfrown
smallint smallmatrixquantity smallsetminus smallsmile smash smdet smile smqty solidLine space
spadesuit sphericalangle splitdfrac splitfrac spmqty sqcap sqcup sqrt sqsubset sqsubseteq
sqsupset sqsupseteq square stackbin stackrel star string strut style subset subseteq subseteqq
subsetneq subsetneqq substack succ succapprox succcurlyeq succeq succnapprox succneqq succnsim
succsim sum sup supset supseteq supseteqq supsetneq supsetneqq surd svmqty swarrow symbb symbf
symbfcal symbffrak symbfit symbfscr symbfsf symbfsfit symbfsfup symbfup symcal symfrak symit
symnormal symrm symscr symsf symsfit symsfup symtt symup tag tan tangent tanh tau tbinom text
textbf textclap textcolor textit textllap textnormal textrlap textrm textsf textstyle texttip
texttt textup tfrac the therefore theta thickapprox thicksim thinspace tilde times tiny to
toggle top tr trace triangle triangledown triangleleft trianglelefteq triangleq triangleright
trianglerighteq tripledash tt twoheadleftarrow twoheadrightarrow underbrace underbracket
underleftarrow underleftrightarrow underline underparen underrightarrow underset unicode
unitfrac units unlhd unrhd upalpha upbeta upchi updelta upepsilon upeta upgamma upharpoonleft
upharpoonright upiota upkappa uplambda uplus upmu upnu upomega upomicron upphi uppi uppsi uprho
uproot upsigma upsilon uptau uptheta upuparrows upupsilon upvarepsilon upvarphi upvarpi upvarrho
upvarsigma upvartheta upxi upzeta usepackage usetagform vDash va var varDelta varGamma varLambda
varOmega varPhi varPi varPsi varSigma varTheta varUpsilon varXi varepsilon variation varinjlim
varkappa varliminf varlimsup varnothing varphi varpi varprojlim varpropto varrho varsigma
varsubsetneq varsubsetneqq varsupsetneq varsupsetneqq vartheta vartriangle vartriangleleft
vartriangleright vb vbox vcenter vcentercolon vdash vdot vdots vdotswithin vec vectorarrow
vectorbold vectorunit vee veebar vmqty vnabla vphantom vqty vtop vu wedge widehat widetilde wp
wr xLeftarrow xLeftrightarrow xLongleftarrow xLongrightarrow xRightarrow xcancel xdef
xhookleftarrow xhookrightarrow xi xleftarrow xleftharpoondown xleftharpoonup xleftrightarrow
xleftrightharpoons xlongequal xlongleftarrow xlongrightarrow xmapsto xmat xmathstrut xmatrix
xrightarrow xrightharpoondown xrightharpoonup xrightleftharpoons xtofrom xtwoheadleftarrow
xtwoheadrightarrow yen zeromatrix zeta zmat
`;

const BUILTIN_NAMES: ReadonlySet<string> = new Set(BUILTIN_NAME_LIST.split(/\s+/).filter(Boolean));

/** Names every JavaScript object inherits (`constructor`, `toString`, `__proto__`, ...). */
const OBJECT_NAMES: ReadonlySet<string> = new Set(Object.getOwnPropertyNames(Object.prototype));

const hasOwn = (target: object, key: string): boolean => Object.prototype.hasOwnProperty.call(target, key);

const isLetterCode = (code: number): boolean => (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
const isLetterAt = (text: string, index: number): boolean => index < text.length && isLetterCode(text.charCodeAt(index));
/** C0 controls (tab, newline, form feed, ...), DEL and C1 controls. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Read an own data property without touching the prototype chain or running getters. */
function ownValue(target: object, key: string): unknown {
  const descriptor = Object.getOwnPropertyDescriptor(target, key);
  return descriptor && 'value' in descriptor ? descriptor.value : undefined;
}

/** Names of control words used in `tex` (`\\` and control symbols skipped). */
function usedNames(tex: string): string[] {
  const names: string[] = [];
  for (let i = 0; i < tex.length; i++) {
    if (tex[i] !== '\\') continue;
    if (isLetterAt(tex, i + 1)) {
      let j = i + 1;
      while (isLetterAt(tex, j)) j++;
      names.push(tex.slice(i + 1, j));
      i = j - 1;
    } else {
      i++;
    }
  }
  return names;
}

function bracesBalanced(text: string): boolean {
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '\\') i++;
    else if (ch === '{') depth++;
    else if (ch === '}' && --depth < 0) return false;
  }
  return depth === 0;
}

function entryProblems(name: string, value: unknown): MathMacroProblem[] {
  const problems: MathMacroProblem[] = [];
  const problem = (code: MathMacroProblemCode, detail?: string): void => {
    problems.push({ name, code, ...(detail ? { detail } : {}) });
  };
  if (OBJECT_NAMES.has(name)) problem('name-reserved-object');
  else if (!/^[A-Za-z]+$/.test(name) || name.length > MAX_MACRO_NAME_LENGTH) problem('name-invalid');
  else if (BUILTIN_NAMES.has(name)) problem('name-builtin');

  if (!isPlainObject(value)) {
    problem('entry-invalid');
    return problems;
  }
  const rawArgs = hasOwn(value, 'args') ? ownValue(value, 'args') : 0;
  const args = typeof rawArgs === 'number' && Number.isInteger(rawArgs) && rawArgs >= 0 && rawArgs <= 9 ? rawArgs : -1;
  if (args < 0) problem('args-invalid');
  const body = hasOwn(value, 'body') ? ownValue(value, 'body') : undefined;
  if (typeof body !== 'string') {
    problem('body-invalid');
    return problems;
  }
  if (body.length > MAX_MACRO_BODY_LENGTH) problem('body-too-large');
  if (CONTROL_CHARS.test(body)) problem('body-control-chars');
  if (!bracesBalanced(body)) problem('unbalanced');
  // numbering belongs to the equation, not to a macro: one body would repeat the label or tag in every use
  if (usedNames(body).some((used) => used === 'label' || used === 'tag')) problem('body-label');
  for (const match of body.matchAll(/#(.?)/gs)) {
    const next = match[1];
    if (next === '#') continue;
    if (!/^[1-9]$/.test(next)) problem('body-invalid', '#');
    else if (args >= 0 && Number(next) > args) problem('body-invalid', `#${next}`);
  }
  return problems;
}

/**
 * Names that sit on a cycle of the "uses" graph (Tarjan's strongly connected components, iterative): a
 * component with two or more names, or a name that uses itself. Linear in names plus edges.
 */
function findCycleMembers(uses: Map<string, string[]>): Set<string> {
  const index = new Map<string, number>();
  const low = new Map<string, number>();
  const onStack = new Set<string>();
  const stack: string[] = [];
  const members = new Set<string>();
  let counter = 0;
  for (const root of uses.keys()) {
    if (index.has(root)) continue;
    const work: { name: string; next: number }[] = [{ name: root, next: 0 }];
    index.set(root, counter);
    low.set(root, counter);
    counter++;
    stack.push(root);
    onStack.add(root);
    while (work.length > 0) {
      const frame = work[work.length - 1];
      const edges = uses.get(frame.name) as string[];
      if (frame.next < edges.length) {
        const to = edges[frame.next++];
        if (!index.has(to)) {
          index.set(to, counter);
          low.set(to, counter);
          counter++;
          stack.push(to);
          onStack.add(to);
          work.push({ name: to, next: 0 });
        } else if (onStack.has(to)) {
          low.set(frame.name, Math.min(low.get(frame.name) as number, index.get(to) as number));
        }
        continue;
      }
      work.pop();
      if (work.length > 0) {
        const parent = work[work.length - 1].name;
        low.set(parent, Math.min(low.get(parent) as number, low.get(frame.name) as number));
      }
      if (low.get(frame.name) === index.get(frame.name)) {
        const component: string[] = [];
        let member: string;
        do {
          member = stack.pop() as string;
          onStack.delete(member);
          component.push(member);
        } while (member !== frame.name);
        if (component.length > 1 || edges.includes(frame.name)) component.forEach((m) => members.add(m));
      }
    }
  }
  return members;
}

interface CheckedTable {
  entries: Map<string, MathMacro>;
  problems: MathMacroProblem[];
}

/** Check an untrusted table; `entries` holds only the entries that passed, as fresh objects. */
function checkTable(input: unknown): CheckedTable {
  const entries = new Map<string, MathMacro>();
  const problems: MathMacroProblem[] = [];
  if (!isPlainObject(input)) {
    return { entries, problems: [{ name: null, code: 'not-an-object' }] };
  }
  let keys: string[];
  try {
    keys = Object.keys(input);
  } catch {
    return { entries, problems: [{ name: null, code: 'not-an-object' }] };
  }
  // The size cap comes first, while collecting, so the cost of everything after is bounded by it.
  let size = 0;
  for (const key of keys) {
    // already over the cap: nothing more is read, validated or kept
    if (size > MAX_MACRO_TABLE_SIZE) {
      problems.push({ name: key, code: 'table-too-large' });
      continue;
    }
    const value = ownValue(input, key);
    const found = entryProblems(key, value);
    if (found.length > 0) {
      problems.push(...found);
      continue;
    }
    const raw = value as Record<string, unknown>;
    const macro = { args: hasOwn(raw, 'args') ? (ownValue(raw, 'args') as number) : 0, body: ownValue(raw, 'body') as string };
    size += key.length + macro.body.length + 1;
    if (size > MAX_MACRO_TABLE_SIZE) {
      problems.push({ name: key, code: 'table-too-large' });
      continue;
    }
    entries.set(key, macro);
  }

  // Macros that use themselves, directly or through others, would never finish expanding. One linear pass
  // (iterative Tarjan search for strongly connected components): a macro is recursive when it sits on a cycle.
  const uses = new Map<string, string[]>();
  for (const [name, macro] of entries) uses.set(name, [...new Set(usedNames(macro.body))].filter((used) => entries.has(used)));
  const onCycle = findCycleMembers(uses);
  for (const name of entries.keys()) {
    if (onCycle.has(name)) problems.push({ name, code: 'recursive' });
  }
  for (const name of onCycle) entries.delete(name);
  return { entries, problems };
}

/**
 * Check a macro table and list what is wrong, per entry. Never throws; an unreadable table gives
 * one `not-an-object` problem. Checks: names (letters only, not an `Object.prototype` name, not a
 * TeX or MathJax built-in), `args` (integer 0 to 9), body (size, control characters, balanced
 * braces, no `\label` or `\tag`, `#n` within `args`), recursion and the size of the whole table.
 */
export function validateMacros(table: unknown): MathMacroProblem[] {
  return checkTable(table).problems;
}

/**
 * Read an untrusted object (for example metadata JSON) into a NEW macro table that holds only the
 * valid entries, and say what was left out. The result has no prototype, and nothing is ever
 * assigned through a key before it has been validated.
 */
export function sanitizeMacroTable(input: unknown): { table: MathMacroTable; problems: MathMacroProblem[] } {
  const { entries, problems } = checkTable(input);
  const table: MathMacroTable = Object.create(null);
  for (const [name, macro] of entries) table[name] = { args: macro.args, body: macro.body };
  return { table, problems };
}

// ---------------------------------------------------------------------------------------------
// Definitions inside TeX text

const DEFINERS: ReadonlySet<string> = new Set([
  'newcommand',
  'renewcommand',
  'providecommand',
  'DeclareRobustCommand',
  'DeclareMathOperator',
  'def',
  'gdef',
  'edef',
  'xdef',
]);

const skipSpaces = (text: string, from: number): number => {
  let i = from;
  while (i < text.length && /\s/.test(text[i])) i++;
  return i;
};

/** The end (exclusive) of the balanced `{...}` group starting at `from`, or -1. */
function groupEnd(text: string, from: number): number {
  if (text[from] !== '{') return -1;
  let depth = 0;
  for (let i = from; i < text.length; i++) {
    const ch = text[i];
    if (ch === '\\') i++;
    else if (ch === '{') depth++;
    else if (ch === '}' && --depth === 0) return i + 1;
  }
  return -1;
}

/** The end (exclusive) of one control sequence starting at `from` (`\name` or `\x`), or -1. */
function controlSequenceEnd(text: string, from: number): number {
  if (text[from] !== '\\' || from + 1 >= text.length) return -1;
  let j = from + 1;
  if (!isLetterAt(text, j)) return j + 1;
  while (isLetterAt(text, j)) j++;
  return j;
}

interface ScannedDefinition {
  start: number;
  end: number;
  name?: string;
  macro?: MathMacro;
  /** `\providecommand` only defines a name that is not defined yet. */
  provide?: boolean;
  problems: MathMacroProblem[];
}

const snippet = (text: string, start: number, end: number): string => text.slice(start, Math.min(end, start + 80));

/** Parse the definition whose command starts at `start`; `nameEnd` is just after the command name. */
function scanDefinition(text: string, start: number, nameEnd: number, command: string): ScannedDefinition {
  const fail = (end: number, code: MathMacroProblemCode, name: string | null = null): ScannedDefinition => ({
    start,
    end: Math.max(end, nameEnd),
    problems: [{ name, code, detail: snippet(text, start, end) }],
  });
  let i = nameEnd;
  if (command === 'let') {
    // `\let\a\b` and `\let\a=\b`: \a becomes another name for \b. Only a control sequence is accepted as the target;
    // `\let\a x` (a character) is refused rather than turned into a macro.
    i = skipSpaces(text, i);
    const nameEnd2 = controlSequenceEnd(text, i);
    const letName = nameEnd2 < 0 ? null : /^\\([A-Za-z]+)$/.exec(text.slice(i, nameEnd2));
    if (!letName) return fail(nameEnd2 < 0 ? i : nameEnd2, nameEnd2 < 0 ? 'syntax' : 'name-invalid', nameEnd2 < 0 ? null : text.slice(i + 1, nameEnd2));
    i = skipSpaces(text, nameEnd2);
    if (text[i] === '=') i = skipSpaces(text, i + 1);
    const to = controlSequenceEnd(text, i);
    if (to < 0) return fail(Math.min(text.length, i + 1), 'syntax', letName[1]);
    const macro: MathMacro = { args: 0, body: text.slice(i, to) };
    return { start, end: to, name: letName[1], macro, problems: entryProblems(letName[1], macro) };
  }
  const star = command !== 'def' && command !== 'gdef' && command !== 'edef' && command !== 'xdef' && text[i] === '*';
  if (star) i++;
  i = skipSpaces(text, i);

  // The macro being defined: `{\name}` or `\name` (`\name` only for the primitives).
  let target: string;
  let afterTarget: number;
  if (text[i] === '{' && !/^(def|gdef|edef|xdef)$/.test(command)) {
    const close = groupEnd(text, i);
    if (close < 0) return fail(text.length, 'unbalanced');
    target = text.slice(i + 1, close - 1).trim();
    afterTarget = close;
  } else {
    const end = controlSequenceEnd(text, i);
    if (end < 0) return fail(i, 'syntax');
    target = text.slice(i, end);
    afterTarget = end;
  }
  const nameMatch = /^\\([A-Za-z]+)$/.exec(target);
  if (!nameMatch) return fail(afterTarget, 'name-invalid', target.replace(/^\\/, ''));
  const name = nameMatch[1];

  let args = 0;
  let cursor = afterTarget;
  const isPrimitive = /^(def|gdef|edef|xdef)$/.test(command);
  let optionalDefault = false;
  if (isPrimitive) {
    const brace = (() => {
      for (let k = cursor; k < text.length; k++) {
        if (text[k] === '\\') k++;
        else if (text[k] === '{') return k;
      }
      return -1;
    })();
    if (brace < 0) return fail(text.length, 'syntax', name);
    const params = text.slice(cursor, brace).trim();
    if (params !== '') {
      const count = params.length / 2;
      const ok = /^(?:#[1-9])+$/.test(params) && params === Array.from({ length: count }, (_, k) => `#${k + 1}`).join('');
      if (!ok || count > 9) return fail(brace, 'unsupported-parameters', name);
      args = count;
    }
    cursor = brace;
  } else if (command !== 'DeclareMathOperator') {
    cursor = skipSpaces(text, cursor);
    if (text[cursor] === '[') {
      const close = text.indexOf(']', cursor);
      if (close < 0) return fail(text.length, 'syntax', name);
      const count = text.slice(cursor + 1, close).trim();
      if (!/^[0-9]$/.test(count)) return fail(close + 1, 'args-invalid', name);
      args = Number(count);
      cursor = skipSpaces(text, close + 1);
      if (text[cursor] === '[') {
        // `[default]` for an optional first argument: not supported.
        const closeDefault = text.indexOf(']', cursor);
        optionalDefault = true;
        cursor = closeDefault < 0 ? text.length : skipSpaces(text, closeDefault + 1);
      }
    }
  } else {
    cursor = skipSpaces(text, cursor);
  }
  cursor = isPrimitive ? cursor : skipSpaces(text, cursor);
  const close = groupEnd(text, cursor);
  if (close < 0) return fail(text.length, 'unbalanced', name);
  if (optionalDefault) return fail(close, 'optional-argument', name);

  let body = text.slice(cursor + 1, close - 1);
  if (command === 'DeclareMathOperator') body = `\\operatorname${star ? '*' : ''}{${body}}`;
  body = body.replace(/\s*[\r\n]+\s*/g, ' ').trim();
  const macro: MathMacro = { args, body };
  const problems = entryProblems(name, macro);
  return { start, end: close, name, macro, provide: command === 'providecommand', problems };
}

function scanDefinitions(text: string): ScannedDefinition[] {
  const found: ScannedDefinition[] = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] !== '\\') continue;
    if (!isLetterAt(text, i + 1)) {
      i++;
      continue;
    }
    let j = i + 1;
    while (isLetterAt(text, j)) j++;
    const command = text.slice(i + 1, j);
    if (!DEFINERS.has(command) && command !== 'let') {
      i = j - 1;
      continue;
    }
    const definition = scanDefinition(text, i, j, command);
    found.push(definition);
    i = definition.end - 1;
  }
  return found;
}

/**
 * Read macro definitions (`\newcommand`, `\renewcommand`, `\providecommand`, `\def`, `\let\a\b`,
 * `\DeclareMathOperator`, the starred forms) out of `text`.
 *
 * - `macros` holds the valid definitions; a later definition replaces an earlier one, and
 *   `\providecommand` never replaces.
 * - `rest` is the text with those definitions removed (runs of blank lines collapse, the ends are trimmed).
 * - `problems` lists definitions that could not be used: unbalanced braces, an optional argument
 *   (`[default]`, not supported), names that are not letters only or are built in, and so on. Such
 *   a definition is left in `rest`, unchanged, so nothing is lost.
 *
 * Line breaks inside a body become single spaces.
 */
export function parseMacroDefinitions(text: string): {
  macros: MathMacroTable;
  rest: string;
  problems: MathMacroProblem[];
} {
  const entries = new Map<string, MathMacro>();
  const problems: MathMacroProblem[] = [];
  let rest = '';
  let last = 0;
  for (const definition of scanDefinitions(text)) {
    if (definition.problems.length > 0 || !definition.name || !definition.macro) {
      problems.push(...definition.problems);
      continue;
    }
    if (!(definition.provide && entries.has(definition.name))) entries.set(definition.name, definition.macro);
    rest += text.slice(last, definition.start);
    last = definition.end;
  }
  rest += text.slice(last);
  const macros: MathMacroTable = Object.create(null);
  for (const [name, macro] of entries) macros[name] = macro;
  return { macros, rest: rest.replace(/\n{3,}/g, '\n\n').trim(), problems };
}

/**
 * The valid macro definitions written inside one equation, with their position in `tex`
 * (`start` inclusive, `end` exclusive). Definitions that cannot be used are not listed.
 */
export function findLocalDefinitions(tex: string): { name: string; macro: MathMacro; start: number; end: number }[] {
  return scanDefinitions(tex)
    .filter((d) => d.problems.length === 0 && d.name !== undefined && d.macro !== undefined)
    .map((d) => ({ name: d.name as string, macro: d.macro as MathMacro, start: d.start, end: d.end }));
}

// ---------------------------------------------------------------------------------------------
// Expansion

/** Whether `text` ends with a control word, so a letter appended directly would extend its name. */
function endsWithControlWord(text: string): boolean {
  const last = text.charCodeAt(text.length - 1);
  if (!isLetterCode(last)) return false;
  return /(?:^|[^\\])(?:\\\\)*\\[A-Za-z]+$/.test(text.slice(-200));
}

/** How much of the end of the output `endsWithControlWord` looks at. */
const TAIL_LENGTH = 200;

/**
 * The expansion's output, built from pieces. It keeps the last `TAIL_LENGTH` characters apart, so asking whether
 * the output ends in a control word costs the same at any length: reading the end of one growing string makes the
 * engine flatten it on every append, which made long equations quadratic.
 */
class Output {
  private readonly parts: string[] = [];
  private tail = '';
  length = 0;

  /** Add `piece` as it is. */
  push(piece: string): void {
    if (piece === '') return;
    this.parts.push(piece);
    this.length += piece.length;
    this.tail = piece.length >= TAIL_LENGTH ? piece.slice(-TAIL_LENGTH) : (this.tail + piece).slice(-TAIL_LENGTH);
  }

  /** Add `piece`, with a space where a control word would otherwise swallow a letter. */
  append(piece: string): void {
    if (piece === '') return;
    if (isLetterCode(piece.charCodeAt(0)) && this.endsWithControlWord()) this.push(' ');
    this.push(piece);
  }

  endsWithControlWord(): boolean {
    return endsWithControlWord(this.tail);
  }

  toString(): string {
    return this.parts.join('');
  }
}

function readArgument(text: string, from: number, budget: Budget): { text: string; end: number } | null {
  if (from >= text.length) return null;
  const ch = text[from];
  if (ch === '{') {
    const end = groupEnd(text, from);
    // scanning is charged too, so an argument group that never closes cannot be rescanned by every call
    budget.left -= (end < 0 ? text.length : end) - from;
    if (budget.left < 0) throw new ExpansionTooLarge();
    return end < 0 ? null : { text: text.slice(from + 1, end - 1), end };
  }
  if (ch === '}') return null;
  if (ch === '\\') {
    const end = controlSequenceEnd(text, from);
    return end < 0 ? null : { text: text.slice(from, end), end };
  }
  const code = text.codePointAt(from) as number;
  const end = from + (code > 0xffff ? 2 : 1);
  return { text: text.slice(from, end), end };
}

/** End of a definition that must be copied through unexpanded, starting after the command name. */
function definitionEnd(text: string, nameEnd: number, command: string): number {
  const scanned = scanDefinition(text, nameEnd - command.length - 1, nameEnd, command);
  if (scanned.end > nameEnd) return scanned.end;
  return nameEnd;
}

function lookup(table: object, name: string): MathMacro | null {
  if (OBJECT_NAMES.has(name) || BUILTIN_NAMES.has(name) || !hasOwn(table, name)) return null;
  const value = ownValue(table, name);
  if (!isPlainObject(value)) return null;
  const body = hasOwn(value, 'body') ? ownValue(value, 'body') : undefined;
  const rawArgs = hasOwn(value, 'args') ? ownValue(value, 'args') : 0;
  if (typeof body !== 'string' || typeof rawArgs !== 'number' || !Number.isInteger(rawArgs) || rawArgs < 0 || rawArgs > 9) {
    return null;
  }
  return { args: rawArgs, body };
}

class ExpansionTooLarge extends Error {}

/** What one `expandMacros` call may still spend: output length and scanned characters (`left`), macro calls (`steps`). */
interface Budget {
  left: number;
  steps: number;
  shadowed: Set<string>;
}

function substitute(body: string, args: readonly string[], budget: Budget): string {
  // The size of the result is known before anything is built: refuse when it would not fit what is left.
  let size = body.length;
  for (let hash = body.indexOf('#'); hash >= 0; hash = body.indexOf('#', hash + 2)) {
    const next = body[hash + 1];
    if (next >= '1' && next <= '9' && Number(next) <= args.length) size += args[Number(next) - 1].length;
    if (size > budget.left) throw new ExpansionTooLarge();
  }
  const out = new Output();
  let i = 0;
  while (i < body.length) {
    const hash = body.indexOf('#', i);
    if (hash < 0) {
      out.append(body.slice(i));
      break;
    }
    out.append(body.slice(i, hash));
    const next = body[hash + 1];
    if (next === '#') {
      out.push('#');
      i = hash + 2;
    } else if (next !== undefined && next >= '1' && next <= '9' && Number(next) <= args.length) {
      out.append(args[Number(next) - 1]);
      i = hash + 2;
    } else {
      out.push('#');
      i = hash + 1;
    }
  }
  return out.toString();
}

/** End of a `\let\name=\other` (the `=` is optional) starting after `\let`; adds `name` to `shadowed`. */
function letEnd(text: string, afterLet: number, shadowed: Set<string>): number {
  let k = skipSpaces(text, afterLet);
  const targetEnd = controlSequenceEnd(text, k);
  if (targetEnd < 0) return afterLet;
  const target = /^\\([A-Za-z]+)$/.exec(text.slice(k, targetEnd));
  if (!target) return afterLet;
  shadowed.add(target[1]);
  k = skipSpaces(text, targetEnd);
  if (text[k] === '=') k = skipSpaces(text, k + 1);
  if (k >= text.length) return targetEnd;
  if (text[k] === '\\') {
    const end = controlSequenceEnd(text, k);
    return end < 0 ? targetEnd : end;
  }
  const code = text.codePointAt(k) as number;
  return k + (code > 0xffff ? 2 : 1);
}

function expandText(src: string, table: object, depth: number, budget: Budget): string {
  const out = new Output();
  let i = 0;
  const n = src.length;
  while (i < n) {
    const backslash = src.indexOf('\\', i);
    if (backslash < 0) {
      out.append(src.slice(i));
      break;
    }
    if (backslash > i) out.append(src.slice(i, backslash));
    i = backslash;
    if (!isLetterAt(src, i + 1)) {
      // `\\`, `\{`, `\,` and a lone trailing backslash are copied as they are.
      out.push(src.slice(i, i + 2));
      i += 2;
      continue;
    }
    let j = i + 1;
    while (isLetterAt(src, j)) j++;
    const name = src.slice(i + 1, j);
    if (DEFINERS.has(name)) {
      // A definition written inside the equation wins over a document macro of the same name from here
      // to the end of the equation, as in TeX: the name is no longer expanded from the table.
      const defined = scanDefinition(src, i, j, name);
      if (defined.name !== undefined && !defined.provide) budget.shadowed.add(defined.name);
      const end = definitionEnd(src, j, name);
      out.append(src.slice(i, end));
      i = end;
      continue;
    }
    if (name === 'let') {
      // `\let\name=\other` (or without `=`): copied through untouched, and `name` is shadowed for the rest of
      // the equation, like `\def`. Expanding the target would turn it into an invalid `\let\mathbb{R}...`.
      const end = letEnd(src, j, budget.shadowed);
      out.append(src.slice(i, end));
      i = end;
      continue;
    }
    const macro = budget.shadowed.has(name) ? null : lookup(table, name);
    if (!macro || depth >= MAX_MACRO_DEPTH) {
      out.append(src.slice(i, j));
      i = j;
      continue;
    }
    if (++budget.steps > MAX_EXPANSION_STEPS) throw new ExpansionTooLarge();
    const args: string[] = [];
    let k = j;
    let complete = true;
    for (let a = 0; a < macro.args; a++) {
      const argument = readArgument(src, skipSpaces(src, k), budget);
      if (!argument) {
        complete = false;
        break;
      }
      args.push(argument.text);
      k = argument.end;
    }
    if (!complete) {
      // Not enough arguments: leave the call alone.
      out.append(src.slice(i, j));
      i = j;
      continue;
    }
    if (out.length > MAX_EXPANDED_LENGTH) throw new ExpansionTooLarge();
    const expanded = expandText(substitute(macro.body, args, budget), table, depth + 1, budget);
    // every expansion costs at least one unit, so macros that expand to nothing cannot fan out for free
    budget.left -= expanded.length + 1;
    if (budget.left < 0) throw new ExpansionTooLarge();
    out.append(expanded);
    i = k;
    // The expansion may end with a control word that now touches a letter of the text that follows.
    if (isLetterAt(src, i) && out.endsWithControlWord()) out.push(' ');
  }
  return out.toString();
}

/**
 * Expand document macros in `tex`: `\name` and `\name{a}{b}` become the macro's body, with `#1` to
 * `#9` replaced by the arguments, nested macros expanded too (up to `MAX_MACRO_DEPTH`).
 *
 * The result is textual, as in TeX: `\R^2` with `R` = `\mathbb{R}` gives `\mathbb{R}^2`. Names are read
 * as whole control words, so `\Ra` is not `\R` followed by `a`, and a space is added where an
 * expansion ending in a control word would otherwise swallow a following letter (`\eps x` with
 * `\varepsilon` stays `\varepsilon x`).
 *
 * Left untouched: control symbols and `\\`, unknown commands, definitions written inside the
 * equation (`\newcommand`, `\def`, ...; and a name defined there is no longer expanded from the
 * table for the rest of that equation, as in TeX), a call with too few arguments, and names that are
 * built in or inherited by every object (a table is never trusted: use `sanitizeMacroTable` first).
 * With no usable macro the input comes back unchanged, byte for byte. If an expansion grows beyond
 * `MAX_EXPANDED_LENGTH`, or takes more than `MAX_EXPANSION_STEPS` macro calls (a macro that expands to
 * nothing counts), the TeX is returned unexpanded.
 */
export function expandMacros(tex: string, table: MathMacroTable | null | undefined): string {
  if (!tex || tex.indexOf('\\') < 0 || !table || typeof table !== 'object') return tex;
  if (Object.keys(table).length === 0) return tex;
  try {
    return expandText(tex, table, 0, { left: MAX_EXPANDED_LENGTH, steps: 0, shadowed: new Set<string>() });
  } catch {
    // too large, or anything unexpected (a RangeError for a huge string): the TeX comes back as it was
    return tex;
  }
}

/**
 * LaTeX preamble lines for a macro table: for each valid macro (sorted by name) a `\providecommand`
 * followed by a `\renewcommand`, so the macro is defined whether or not the name existed, and a
 * class that already uses the name is overridden. Invalid entries are skipped.
 */
export function toLatexPreamble(table: MathMacroTable | null | undefined): string {
  const { entries } = checkTable(table);
  const lines: string[] = [];
  for (const name of [...entries.keys()].sort()) {
    const macro = entries.get(name) as MathMacro;
    const signature = macro.args > 0 ? `[${macro.args}]` : '';
    lines.push(`\\providecommand{\\${name}}${signature}{${macro.body}}`);
    lines.push(`\\renewcommand{\\${name}}${signature}{${macro.body}}`);
  }
  return lines.join('\n');
}
